import { backendApi } from "./backendApi.rtkq";

export interface CaseListItem {
  caseId: string;
  title: string;
  description: string;
  yodFatId: string;
  status: string;
  legalSides?: string;
  officeId?: string;
  clientId?: string;
  caseNumber?: string;
  createAt?: string;
  updatedAt?: string;
}

export interface CaseAssignment {
  userId: string;
  email: string;
  displayName: string;
  permission: string;
  assignedBy?: string;
}

export interface CaseDetail extends CaseListItem {
  assignments: CaseAssignment[];
  client?: any;
  documents?: any[];
}

export interface CaseListResponse {
  cases: CaseListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const casesApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    listCases: builder.query<
      CaseListResponse,
      {
        officeId: string;
        status?: string;
        search?: string;
        clientId?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ officeId, ...params }) => ({
        url: `/offices/${officeId}/cases`,
        params,
      }),
      providesTags: ["Cases"],
    }),

    getCase: builder.query<CaseDetail, { officeId: string; caseId: string }>({
      query: ({ officeId, caseId }) =>
        `/offices/${officeId}/cases/${caseId}`,
      providesTags: (_r, _e, { caseId }) => [{ type: "Cases", id: caseId }],
    }),

    createCase: builder.mutation<
      CaseListItem,
      {
        officeId: string;
        title: string;
        description?: string;
        yodFatId?: string;
        legalSides?: string;
        clientId?: string;
        caseNumber?: string;
      }
    >({
      query: ({ officeId, ...body }) => ({
        url: `/offices/${officeId}/cases`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Cases"],
    }),

    updateCase: builder.mutation<
      CaseListItem,
      {
        officeId: string;
        caseId: string;
        title?: string;
        description?: string;
        status?: string;
        legalSides?: string;
        clientId?: string;
        caseNumber?: string;
      }
    >({
      query: ({ officeId, caseId, ...body }) => ({
        url: `/offices/${officeId}/cases/${caseId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        "Cases",
        { type: "Cases", id: caseId },
      ],
    }),

    assignUserToCase: builder.mutation<
      any,
      {
        officeId: string;
        caseId: string;
        userId: string;
        permission: string;
      }
    >({
      query: ({ officeId, caseId, userId, permission }) => ({
        url: `/offices/${officeId}/cases/${caseId}/assignments`,
        method: "POST",
        body: { userId, permission },
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "Cases", id: caseId },
        "CaseAssignments",
      ],
    }),

    removeUserFromCase: builder.mutation<
      any,
      { officeId: string; caseId: string; userId: string }
    >({
      query: ({ officeId, caseId, userId }) => ({
        url: `/offices/${officeId}/cases/${caseId}/assignments/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "Cases", id: caseId },
        "CaseAssignments",
      ],
    }),

    getCaseAssignments: builder.query<
      CaseAssignment[],
      { officeId: string; caseId: string }
    >({
      query: ({ officeId, caseId }) =>
        `/offices/${officeId}/cases/${caseId}/assignments`,
      providesTags: ["CaseAssignments"],
    }),

    listAccessRequests: builder.query<
      Array<{
        id: string;
        caseId: string;
        userId: string;
        requestedPermission: string;
        message?: string;
        status: string;
        createdAt: string;
        user?: { displayName: string; email: string };
        case?: { title: string; caseNumber: number };
      }>,
      { officeId: string; status?: string }
    >({
      query: ({ officeId, status }) => ({
        url: `/offices/${officeId}/cases/access-requests`,
        params: status ? { status } : undefined,
      }),
      providesTags: ["AccessRequests"],
    }),

    approveAccessRequest: builder.mutation<
      any,
      { officeId: string; requestId: string }
    >({
      query: ({ officeId, requestId }) => ({
        url: `/offices/${officeId}/cases/access-requests/${requestId}/approve`,
        method: "PUT",
      }),
      invalidatesTags: ["AccessRequests", "Cases", "CaseAssignments"],
    }),

    denyAccessRequest: builder.mutation<
      any,
      { officeId: string; requestId: string }
    >({
      query: ({ officeId, requestId }) => ({
        url: `/offices/${officeId}/cases/access-requests/${requestId}/deny`,
        method: "PUT",
      }),
      invalidatesTags: ["AccessRequests"],
    }),

    bulkAssignCases: builder.mutation<
      {
        results: Array<{
          caseId: string;
          userId: string;
          status: "created" | "updated" | "error";
          error?: string;
        }>;
      },
      {
        officeId: string;
        assignments: Array<{
          caseId: string;
          userId: string;
          permission: string;
        }>;
      }
    >({
      query: ({ officeId, assignments }) => ({
        url: `/offices/${officeId}/cases/bulk-assignments`,
        method: "POST",
        body: { assignments },
      }),
      invalidatesTags: ["Cases", "CaseAssignments"],
    }),
  }),
});

export const {
  useListCasesQuery,
  useGetCaseQuery,
  useCreateCaseMutation,
  useUpdateCaseMutation,
  useAssignUserToCaseMutation,
  useRemoveUserFromCaseMutation,
  useGetCaseAssignmentsQuery,
  useBulkAssignCasesMutation,
  useListAccessRequestsQuery,
  useApproveAccessRequestMutation,
  useDenyAccessRequestMutation,
} = casesApi;
